import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/libs/utils/cn';

interface CrmSearchFieldProps {
  id: string;
  /** Names the field for assistive tech: the design shows no visible label above it. */
  label: string;
  icon: ReactNode;
  placeholder: string;
  value: string;
  /** Characters the term needs before searching is offered. Gates the button, not the input. */
  minSearchLength?: number;
  onChange: (value: string) => void;
  onSearch: () => void;
}

/**
 * The CRM lookup field: an icon, a borderless input and its Search button in one bordered row.
 * Searching never fires as the BD types — the portal's search index is rate-limited — so it takes
 * an explicit act: the button, or Enter.
 */
export function CrmSearchField({
  id,
  label,
  icon,
  placeholder,
  value,
  minSearchLength = 1,
  onChange,
  onSearch,
}: CrmSearchFieldProps) {
  const searchable = value.trim().length >= minSearchLength;
  const search = () => {
    if (!searchable) return;
    onSearch();
  };

  return (
    <div className="flex h-[42px] items-center gap-2 rounded-lg border border-border pr-1.5 pl-3 focus-within:border-brand-700">
      <span className="flex shrink-0 items-center text-primary-600 [&_svg]:size-4">{icon}</span>
      <input
        id={id}
        aria-label={label}
        autoComplete="off"
        // Keeps the password managers' autofill off a CRM form they mistake for a signup.
        data-form-type="other"
        data-1p-ignore
        data-lpignore="true"
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.code !== 'Enter' && event.code !== 'NumpadEnter') return;
          event.preventDefault();
          search();
        }}
        className="min-w-0 flex-1 bg-transparent text-sm text-primary-default outline-none placeholder:text-primary-400"
      />
      <button
        type="button"
        onClick={search}
        disabled={!searchable}
        className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-brand-800 transition-opacity hover:opacity-70 disabled:pointer-events-none disabled:text-primary-400"
      >
        Search
      </button>
    </div>
  );
}

interface CrmRecordRowProps {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  onSelect: () => void;
}

/** One match in the result list. */
export function CrmRecordRow({ icon, title, subtitle, onSelect }: CrmRecordRowProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-center gap-2.5 rounded-lg p-2.5 text-left hover:bg-slate-50"
    >
      <RecordIcon className="size-8 rounded-lg bg-slate-50">{icon}</RecordIcon>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] text-primary-default">{title}</span>
        {subtitle && (
          <span className="block truncate text-[11.5px] text-primary-500">{subtitle}</span>
        )}
      </span>
    </button>
  );
}

interface SelectedRecordProps {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  clearLabel: string;
  onClear: () => void;
}

/**
 * The chosen record, collapsed into a chip that replaces the search: what the deal will attach to
 * is one line, and changing it is one click.
 */
export function SelectedRecord({
  icon,
  title,
  subtitle,
  clearLabel,
  onClear,
}: SelectedRecordProps) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg bg-slate-50 p-2.5">
      <RecordIcon className="size-[30px] rounded-[7px] bg-white">{icon}</RecordIcon>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-semibold text-primary-default">
          {title}
        </span>
        {subtitle && (
          <span className="block truncate text-[11.5px] text-primary-500">{subtitle}</span>
        )}
      </span>
      <button
        type="button"
        aria-label={clearLabel}
        onClick={onClear}
        className="shrink-0 rounded-md p-1 text-primary-500 hover:bg-white hover:text-primary-700"
      >
        <X className="size-3.5" aria-hidden />
      </button>
    </div>
  );
}

/** Empty state for a search that matched nothing. */
export function CrmNoMatch({ children }: { children: ReactNode }) {
  return <p className="px-1 py-3.5 text-center text-sm text-primary-500">{children}</p>;
}

function RecordIcon({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center text-brand-800 [&_svg]:size-[15px]',
        className,
      )}
    >
      {children}
    </span>
  );
}
